import React from 'react';
import { Box, ButtonBase, Paper, Typography } from '@mui/material';
import { GetInterventionLadderResponse, InterventionActivitiesMissing, InterventionLadderPeriod, InterventionLadderStepModel } from '../../../api-service/clients';
import { CircularProgressCentered } from '../../../components/circular-progress-centered';
import { formatNumber } from '../../../utilities';
import { stepIntensity } from './intervention-ladder-colors';
import { StepCardHeading, stepCardColors, stepCardDivider, stepCardFrame, StepNumber } from './intervention-ladder-step-card';
import { formatActivitiesWithoutPrice, formatMillions, InterventionLadderStepCost } from './intervention-cost';
import { InterventionLadderCaption } from './intervention-ladder-caption';
import { InterventionLadderStaircase } from './intervention-ladder-staircase';
import { formatActivitiesWithUnknownSupplier, InterventionLadderStepSuppliers } from './intervention-suppliers';
import { ShowActivitiesLink } from './intervention-activities-dialog';

// Each section of a step is a row shared by all steps, so the lines of the steps align.
const movesSectionCount = 3;

const stepSectionCount = movesSectionCount + 4;

const warningBackground = '#fdf0d9';

const warningColor = '#8a5300';

function formatNetMoves(arrivals: number, departures: number) {
  const net = arrivals - departures;
  if (net === 0) {
    return '0';
  }
  return net > 0 ? `+${formatNumber(net)}` : `−${formatNumber(-net)}`;
}

export type InterventionLadderDetails = 'moves' | 'costs' | 'suppliers';

export interface InterventionLadderComponentProps {
  period: InterventionLadderPeriod;
  currentYear: number;
  ladder: GetInterventionLadderResponse | undefined;
  hasFailed: boolean;
  selectedStep: number | undefined;
  details: InterventionLadderDetails;
  onSelectPeriod: (period: InterventionLadderPeriod) => void;
  onSelectStep: (step: InterventionLadderStepModel, intensity: number, details: InterventionLadderDetails) => void;
  onShowActivities: (step: InterventionLadderStepModel, missing: InterventionActivitiesMissing) => void;
}

export function InterventionLadderComponent({
  period,
  currentYear,
  ladder,
  hasFailed,
  selectedStep,
  details,
  onSelectPeriod,
  onSelectStep,
  onShowActivities,
}: InterventionLadderComponentProps) {
  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <InterventionLadderCaption period={period} currentYear={currentYear} ladder={ladder} onSelectPeriod={onSelectPeriod} />
      <Box sx={{ p: 2 }}>
        <InterventionLadderSteps
          ladder={ladder}
          hasFailed={hasFailed}
          selectedStep={selectedStep}
          details={details}
          onSelectStep={onSelectStep}
          onShowActivities={onShowActivities}
        />
      </Box>
    </Paper>
  );
}

interface InterventionLadderStepsProps {
  ladder: GetInterventionLadderResponse | undefined;
  hasFailed: boolean;
  selectedStep: number | undefined;
  details: InterventionLadderDetails;
  onSelectStep: (step: InterventionLadderStepModel, intensity: number, details: InterventionLadderDetails) => void;
  onShowActivities: (step: InterventionLadderStepModel, missing: InterventionActivitiesMissing) => void;
}

function InterventionLadderSteps({ ladder, hasFailed, selectedStep, details, onSelectStep, onShowActivities }: InterventionLadderStepsProps) {
  if (hasFailed) {
    return <Typography>Indsatstrappen kunne ikke hentes.</Typography>;
  }
  if (ladder === undefined) {
    return <CircularProgressCentered />;
  }
  const { steps, total } = ladder;
  if (steps.length === 0) {
    return <Typography>Indsatstrappen er ikke sat op.</Typography>;
  }
  return (
    <>
      <InterventionLadderStaircase steps={steps} selectedStep={selectedStep} />
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, columnGap: 3 }}>
        {steps.map((step, index) => {
          const intensity = stepIntensity(index, steps.length);
          const isSelected = step.step === selectedStep;
          return (
            <InterventionLadderStep
              key={step.step}
              step={step}
              intensity={intensity}
              totalCost={total.cost.total}
              isSelected={isSelected}
              selectedDetails={isSelected ? details : undefined}
              onSelect={selected => onSelectStep(step, intensity, selected)}
              onShowActivities={missing => onShowActivities(step, missing)}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 4,
          rowGap: 1,
          mt: 5,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          fontSize: '0.875rem',
          color: 'text.primary',
          '& strong': { fontWeight: 600 },
        }}
      >
        <span>
          I alt <strong>{formatNumber(total.citizens)}</strong> unikke borgere
        </span>
        <span>
          <strong>{formatNumber(total.personYears, 1)}</strong> årspersoner
        </span>
        <span>
          <strong>{formatMillions(total.cost.total)}</strong> mio. kr.{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            (intern {formatMillions(total.cost.internal)} · ekstern {formatMillions(total.cost.external)})
          </Box>
        </span>
        {total.cost.activitiesWithoutPrice > 0 && <WarningPill text={formatActivitiesWithoutPrice(total.cost.activitiesWithoutPrice)} />}
      </Box>
    </>
  );
}

function WarningPill({ text }: { text: string }) {
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: '999px', bgcolor: warningBackground, color: warningColor, fontWeight: 600 }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: warningColor }} />
      {text}
    </Box>
  );
}

interface InterventionLadderStepProps {
  step: InterventionLadderStepModel;
  intensity: number;
  totalCost: number;
  isSelected: boolean;
  selectedDetails: InterventionLadderDetails | undefined;
  onSelect: (details: InterventionLadderDetails) => void;
  onShowActivities: (missing: InterventionActivitiesMissing) => void;
}

function InterventionLadderStep({ step, intensity, totalCost, isSelected, selectedDetails, onSelect, onShowActivities }: InterventionLadderStepProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridRow: `span ${stepSectionCount}`,
        gridTemplateRows: 'subgrid',
        rowGap: 0,
        ...stepCardFrame(isSelected),
        bgcolor: '#f6f9fb',
        position: 'relative',
        zIndex: isSelected ? 1 : 0,
        // Growing down from the top keeps the space between the staircase and the steps the same.
        transform: isSelected ? 'scale(1.03)' : 'none',
        transformOrigin: 'top center',
        boxShadow: isSelected ? '0 8px 20px rgba(24, 76, 97, 0.25)' : 'none',
        transition: 'transform 150ms, box-shadow 150ms',
      }}
    >
      <InterventionLadderStepMoves step={step} intensity={intensity} isSelected={isSelected} isPressed={selectedDetails === 'moves'} onSelect={() => onSelect('moves')} />
      <InterventionLadderStepCost step={step} totalCost={totalCost} isSelected={selectedDetails === 'costs'} onSelect={() => onSelect('costs')} />
      <Box sx={{ mx: 1.5, borderTop: 1, borderColor: 'divider' }} />
      <InterventionLadderStepSuppliers step={step} isSelected={selectedDetails === 'suppliers'} onSelect={() => onSelect('suppliers')} />
      <InterventionLadderStepActivitiesMissing step={step} onShowActivities={onShowActivities} />
    </Box>
  );
}

interface InterventionLadderStepMovesProps {
  step: InterventionLadderStepModel;
  intensity: number;
  isSelected: boolean;
  isPressed: boolean;
  onSelect: () => void;
}

function InterventionLadderStepMoves({ step, intensity, isSelected, isPressed, onSelect }: InterventionLadderStepMovesProps) {
  const divider = stepCardDivider(intensity);
  return (
    <ButtonBase
      component="div"
      onClick={onSelect}
      aria-pressed={isPressed}
      sx={{
        display: 'grid',
        gridRow: `span ${movesSectionCount}`,
        gridTemplateRows: 'subgrid',
        rowGap: 0,
        alignItems: 'stretch',
        justifyContent: 'normal',
        textAlign: 'left',
        font: 'inherit',
        px: 1.5,
        ...stepCardColors(intensity),
      }}
    >
      <Box sx={{ pt: 1.5, pb: 1.5 }}>
        <StepCardHeading step={step} isSelected={isSelected} />
      </Box>
      <Box sx={{ ...divider, py: 1.5, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', alignItems: 'start', gap: 1 }}>
        <StepNumber value={formatNumber(step.citizens)} label="borgere" />
        <StepNumber value={formatNumber(step.personYears, 1)} label="årspersoner" />
        <StepNumber value={formatNetMoves(step.arrivals, step.departures)} label={`${formatNumber(step.arrivals)} ind · ${formatNumber(step.departures)} ud`} />
      </Box>
      <Box
        component="ul"
        sx={{
          ...divider,
          listStyle: 'none',
          m: 0,
          pt: 1.5,
          px: 0,
          pb: 2,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          alignContent: 'start',
          columnGap: 1.5,
          rowGap: 0.5,
          fontSize: '0.8125rem',
          lineHeight: 1.35,
        }}
      >
        {step.paragraphs.map(paragraph => (
          <Box component="li" key={paragraph.paragraph + paragraph.name} sx={{ display: 'contents' }}>
            <Box component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {paragraph.paragraph}
            </Box>
            <span>{paragraph.name}</span>
          </Box>
        ))}
      </Box>
    </ButtonBase>
  );
}

interface InterventionLadderStepActivitiesMissingProps {
  step: InterventionLadderStepModel;
  onShowActivities: (missing: InterventionActivitiesMissing) => void;
}

function InterventionLadderStepActivitiesMissing({ step, onShowActivities }: InterventionLadderStepActivitiesMissingProps) {
  const withoutPrice = step.cost.activitiesWithoutPrice;
  const withUnknownSupplier = step.suppliers.activitiesWithUnknownSupplier;
  if (withoutPrice === 0 && withUnknownSupplier === 0) {
    return <Box />;
  }
  const isBoth = withoutPrice > 0 && withUnknownSupplier > 0;
  return (
    <Box sx={{ px: 1.5, pb: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>
      {withoutPrice > 0 && (
        <ShowActivitiesLink
          text={isBoth ? `${formatNumber(withoutPrice)} uden pris` : formatActivitiesWithoutPrice(withoutPrice)}
          onShow={() => onShowActivities(InterventionActivitiesMissing.Price)}
        />
      )}
      {isBoth && ' · '}
      {withUnknownSupplier > 0 && (
        <ShowActivitiesLink
          text={isBoth ? `${formatNumber(withUnknownSupplier)} uden kendt leverandør` : formatActivitiesWithUnknownSupplier(withUnknownSupplier)}
          onShow={() => onShowActivities(InterventionActivitiesMissing.Supplier)}
        />
      )}
    </Box>
  );
}
